import { AcceptedWords, ignoreKey, shownIssues, type Dialect, type SpellingIssue } from '@realmery/realm-core';
import { Plugin, PluginKey, type EditorState } from 'prosemirror-state';
import { Decoration, DecorationSet, type EditorView } from 'prosemirror-view';
import { realms } from '../db/client.ts';
import { checkSpelling, myDictionary } from '../spelling/client.ts';

/**
 * Misspellings underlined in an editor (docs/PARITY-STEP-6-PLAN.md section 6), as the
 * desktop's `ProofingOverlay`: the text is split into paragraphs, checked about a second
 * after typing stops, and each misspelling drawn with a wavy line over the text, which
 * never changes the text itself. The realm's names, its dictionary and yours are let
 * through, and so is what was ignored in this scene or entry.
 */

/** A misspelling where it is in the document now. */
export interface Misspelling {
  readonly from: number;
  readonly to: number;
  readonly issue: SpellingIssue;
}

interface SpellingState {
  readonly decorations: DecorationSet;
  readonly found: readonly Misspelling[];
}

export const spellingKey = new PluginKey<SpellingState>('spelling');

/** Underlines, kept in place as the text around them changes until the next check replaces them. */
export function spellingPlugin(): Plugin<SpellingState> {
  return new Plugin<SpellingState>({
    key: spellingKey,
    state: {
      init: () => ({ decorations: DecorationSet.empty, found: [] }),
      apply(tr, old, _before, after) {
        const replaced = tr.getMeta(spellingKey) as readonly Misspelling[] | undefined;
        if (replaced) {
          return {
            found: replaced,
            decorations: DecorationSet.create(
              after.doc,
              replaced.map((m) => Decoration.inline(m.from, m.to, { class: 'spell-error', 'data-spelling': m.issue.text })),
            ),
          };
        }
        if (!tr.docChanged) return old;
        const found = old.found.flatMap((m) => {
          const from = tr.mapping.map(m.from, 1);
          const to = tr.mapping.map(m.to, -1);
          return to > from && after.doc.textBetween(from, to) === m.issue.text ? [{ ...m, from, to }] : [];
        });
        return { found, decorations: old.decorations.map(tr.mapping, after.doc) };
      },
    },
    props: {
      decorations: (state) => spellingKey.getState(state)?.decorations ?? DecorationSet.empty,
    },
  });
}

export const misspellings = (state: EditorState): readonly Misspelling[] => spellingKey.getState(state)?.found ?? [];

/** The misspelling at a position, if there's one there. */
export const misspellingAt = (state: EditorState, pos: number) => misspellings(state).find((m) => pos >= m.from && pos <= m.to) ?? null;

/** Where an editor's checks are remembered: the scene or entry, and what to call it ("Ignore in This Scene"). */
export interface ProofingScope {
  readonly file: string;
  readonly scopeId: string;
  readonly noun: 'Scene' | 'Entry';
}

/** The paragraphs of the text, and where each starts. Inline pictures and line breaks count one place each. */
function paragraphs(state: EditorState): { start: number; text: string }[] {
  const out: { start: number; text: string }[] = [];
  state.doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    out.push({ start: pos + 1, text: node.textBetween(0, node.content.size, undefined, '￼') });
    return false;
  });
  return out;
}

/** Checks the editor's text and underlines what's misspelled, unless the text has changed meanwhile. */
export async function checkEditor(view: EditorView, scope: ProofingScope, dialect: Dialect): Promise<void> {
  const doc = view.state.doc;
  const blocks = paragraphs(view.state);
  const [raw, context] = await Promise.all([checkSpelling(blocks.map((b) => b.text), dialect), realms.proofingContext(scope.file, scope.scopeId)]);
  if (view.isDestroyed || view.state.doc !== doc) return;
  const accepted = new AcceptedWords(context.names, [...context.dictionary, ...myDictionary()], context.ignored);
  const found: Misspelling[] = [];
  blocks.forEach((block, i) => {
    for (const issue of shownIssues(raw[i] ?? [], accepted)) found.push({ from: block.start + issue.start, to: block.start + issue.end, issue });
  });
  view.dispatch(view.state.tr.setMeta(spellingKey, found).setMeta('addToHistory', false));
}

/** Clears the underlines: spelling turned off. */
export function clearSpelling(view: EditorView): void {
  if (misspellings(view.state).length > 0) view.dispatch(view.state.tr.setMeta(spellingKey, []).setMeta('addToHistory', false));
}

export { ignoreKey };
